import React, { useState } from 'react';
import { JobsTabIcon, RedCloseIcon, VerticalListActionsIcon } from 'assets/icons';
import { CustomPopover } from 'components';
import { getInitials } from 'utils/commonUtils';
import CustomAvatar from 'components/avatar/CustomAvatar';
import DateUtils from 'utils/dateUtils';
import { JobStatus } from '../constant';
import { getJobStatusColor, getJobStatusText } from '../utils';
import { RelatedJob } from 'containers/estimates/estimates.model';
import { getAmountWithCurrency } from 'utils/amountUtils';

interface RelatedJobCardProps {
  job: RelatedJob;
  onJobClick: (id: string) => void;
  onRemoveJob: (id: string) => void;
  className?: string;
}

const RelatedJobCard: React.FC<RelatedJobCardProps> = ({ job, onJobClick, onRemoveJob, className }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const togglePopover = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
  };

  return (
    <div
      onClick={e => {
        e.stopPropagation();
        if (job.hasPermission) onJobClick(job.jobId);
      }}
      key={job.jobId}
      className={`flex w-full items-center justify-between gap-1 border-t border-borderGray py-4 
        ${job.hasPermission ? 'cursor-pointer' : 'pointer-events-none'} ${className}`}>
      <div className="flex w-[70%] gap-2">
        <JobsTabIcon className="shrink-0" />
        <div className="w-4/5 grow">
          <div className="text-sbase text-tertiaryText">{job.jobNumber}</div>
          <div className="max-w-[90%] break-words text-17px font-semibold text-primaryText">{job.jobTitle}</div>
          <div className="flex items-center gap-2">
            <div className="text-px13 text-accentText">
              {DateUtils.getDateInFormat({
                date: new Date(job.createdAt),
                dateFormat: 'dd MMM yyyy'
              })}
            </div>
            <div className="h-1 w-1 rounded-full bg-accentText"></div>
            <div>
              <CustomAvatar src={job.profilePicture} width="w-4" height="h-4" fontStyle="text-[8px]">
                {getInitials(job.merchantName)}
              </CustomAvatar>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <div className="text-right">
          {+job.payableAmount > 0 && (
            <div className="text-17px font-bold text-primaryText">{getAmountWithCurrency(job.payableAmount)}</div>
          )}
          <div className="text-px13" style={{ color: getJobStatusColor(job.jobStatus as JobStatus) }}>
            {getJobStatusText(job.jobStatus as JobStatus)}
          </div>
        </div>
        {job.hasPermission && job.isEditable && (
          <CustomPopover
            anchorComponentId={`job-${job.jobId}`}
            anchorComponent={() => (
              <VerticalListActionsIcon
                height={20}
                width={4}
                className={'mx-2 flex shrink-0 cursor-pointer'}
                id="job-option-click"
              />
            )}
            show={isPopoverOpen}
            onTogglePopover={togglePopover}
            onClosePopover={handleClosePopover}
            offset="-right-1"
            minWidth="min-w-[160px]"
            showArrow={true}
            highlightAnchor={true}
            padding="p-3">
            <div
              className="flex cursor-pointer items-center gap-2 hover:bg-highlightGray"
              role="presentation"
              onClick={e => {
                e.stopPropagation();
                onRemoveJob(job.jobId);
                handleClosePopover();
              }}>
              <RedCloseIcon />
              <div className="text-sbase font-semibold text-primaryText">Remove job</div>
            </div>
          </CustomPopover>
        )}
      </div>
    </div>
  );
};

export default RelatedJobCard;
